import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { useRole } from './RoleContext';

const RecycleBin = () => {
  const { isFullAccess: isAdmin } = useRole();
  const [recycleItems, setRecycleItems] = useState([]);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  const formatTk = (num) => new Intl.NumberFormat('en-IN').format(num || 0);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    let d = dateStr?.toDate ? dateStr.toDate() : new Date(dateStr);
    if (isNaN(d.getTime())) {
      const cleaned = dateStr.replace('T', ' ').replace('Z', '');
      d = new Date(cleaned);
    }
    if (isNaN(d.getTime())) return dateStr;

    const day = String(d.getDate()).padStart(2, '0');
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const monthName = months[d.getMonth()];
    const year = d.getFullYear();
    
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${day}-${monthName}-${year}, ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  useEffect(() => {
    const unsubRecycle = onSnapshot(query(collection(db, "recycleBin"), orderBy("deletedAt", "desc")), (snapshot1) => {
      const list1 = snapshot1.docs.map(docSnap => ({ id: docSnap.id, sourceCol: "recycleBin", ...docSnap.data() }));
      
      const unsubDeletedProd = onSnapshot(query(collection(db, "deleted_products"), orderBy("deletedAt", "desc")), (snapshot2) => {
        const list2 = snapshot2.docs.map(docSnap => ({ id: docSnap.id, sourceCol: "deleted_products", ...docSnap.data() }));
        
        const combined = [...list1, ...list2].sort((a, b) => {
          const tA = a.deletedAt?.toDate ? a.deletedAt.toDate() : new Date(0);
          const tB = b.deletedAt?.toDate ? b.deletedAt.toDate() : new Date(0);
          return tB - tA;
        });

        setRecycleItems(combined);
      });

      return () => unsubDeletedProd();
    });

    return () => unsubRecycle();
  }, []);

  // ---- ড্রপডাউন/ডেট বদলালেই সাথে সাথে ফিল্টার হয়ে যায়, আলাদা করে "View" চাপার দরকার নেই ----
  const filteredItems = useMemo(() => {
    return recycleItems.filter(item => {
      const d = item.deletedAt?.toDate ? item.deletedAt.toDate() : (item.date ? new Date(item.date) : null);

      let matchesType = true;
      if (selectedType !== 'all') {
        if (selectedType === 'sales') {
          matchesType = item.originalCollection === 'sales' || item.type === 'sales' || item.invoiceId;
        } else if (selectedType === 'purchases') {
          matchesType = item.originalCollection === 'purchases' || item.type === 'purchases' || item.purchaseId;
        } else if (selectedType === 'stock') {
          matchesType = item.type === 'stock_log' || item.type === 'product' || item.originalCollection === 'products';
        } else if (selectedType === 'returns') {
          matchesType = item.originalCollection === 'product_returns' || item.type === 'product_return';
        } else if (selectedType === 'other') {
          // Anything routed here from HR, settings or courier -- everything that isn't
          // one of the four original categories above.
          const known = ['sales', 'purchases', 'products', 'product_returns'];
          matchesType = !known.includes(item.originalCollection) && item.type !== 'stock_log' && item.type !== 'product';
        }
      }

      if (!startDate && !endDate) return matchesType;
      if (!d) return false;

      const itemDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return matchesType && itemDate >= startDate && itemDate <= endDate;
    });
  }, [recycleItems, selectedType, startDate, endDate]);

  const clearFilter = () => {
    setStartDate('');
    setEndDate('');
    setSelectedType('all');
  };

  const exportExcel = () => {
    const dataToExport = filteredItems.map(item => ({
      DeletedAt: formatDateTime(item.deletedAt),
      ItemDetails: item.name || item.purchaseId || item.invoiceId || 'N/A',
      Type: item.type || item.originalCollection || 'N/A',
      DeletedBy: item.deletedBy || 'Sohan',
      Reason: item.reason || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RecycleBin");
    XLSX.writeFile(wb, "RecycleBin_Report.xlsx");
  };

  // Anyone may look through the bin to see what was deleted and by whom, but putting a
  // document back into live data is an Admin-only action.
  const handleRestore = async (item) => {
    if (!isAdmin) return alert('Only an Admin can restore deleted items.');
    let confirmText = '';
    const itemName = item.name || item.purchaseId || item.invoiceId || 'Item';

    if (item.type === 'stock_log' && item.deletedLog) {
      const logDate = formatDateTime(item.deletedLog.date);
      const qty = item.deletedLog.quantity || 0;
      const rate = item.deletedLog.costPrice || item.deletedLog.price || 0;
      confirmText = `Are you sure you want to restore this stock log?\n\nProduct: ${itemName}\nDate: ${logDate}\nQty: ${qty} Pcs\nRate: ৳ ${formatTk(rate)}`;
    } else {
      confirmText = `Are you sure you want to restore "${itemName}"?`;
    }

    if (window.confirm(confirmText)) {
      try {
        if (item.type === "stock_log") {
          const productRef = doc(db, "products", item.originalId);
          const prodSnap = await getDoc(productRef);

          if (prodSnap.exists()) {
            const prodData = prodSnap.data();
            const currentHistory = prodData.history || [];
            const updatedHistory = [...currentHistory, item.deletedLog];
            const updatedStock = updatedHistory.reduce((sum, e) => sum + (Number(e.quantity) || 0), 0);

            await updateDoc(productRef, { history: updatedHistory, stock: updatedStock });
          } else {
            await setDoc(productRef, {
              name: item.name,
              barcode: item.barcode,
              stock: item.quantity,
              price: item.deletedLog?.costPrice || item.deletedLog?.price || 0,
              history: [item.deletedLog]
            });
          }
        } else {
          const targetCollection = item.originalCollection || 'purchases';
          const originalId = item.originalId || item.id;
          const { id, deletedAt, originalCollection, reason, deletedBy, type, deletedItemType, sourceCol, ...originalData } = item;
          await setDoc(doc(db, targetCollection, originalId), originalData);
        }

        await deleteDoc(doc(db, item.sourceCol || "recycleBin", item.id));
        alert("Restored successfully!");
      } catch (error) {
        console.error("Error restoring item: ", error);
        alert("Failed to restore!");
      }
    }
  };

  return (
    <div className="white-box fade-in" style={{ padding: '30px', borderRadius: '20px', background: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#333' }}>Recycle Bin</h2>
          <p style={{ margin: '5px 0 0 0', color: '#888', fontSize: '13px' }}>
            Everything deleted anywhere in the app is kept here with who deleted it and when.
            {!isAdmin && ' Only an Admin can restore an item.'}
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select 
            value={selectedType} 
            onChange={e => setSelectedType(e.target.value)}
            style={{ padding: '10px', borderRadius: '10px', border: '1px solid #eee', background: '#f8f9ff', outline: 'none', fontWeight: '500' }}
          >
            <option value="all">All Types</option>
            <option value="sales">Sales Bin</option>
            <option value="purchases">Purchase</option>
            <option value="stock">Stock (Product / Log)</option>
            <option value="returns">Product Return</option>
            <option value="other">HR / Settings / Courier</option>
          </select>

          <input 
            type="date" 
            value={startDate} 
            onChange={e => setStartDate(e.target.value)} 
            style={{ padding: '9px', borderRadius: '10px', border: '1px solid #eee', outline: 'none' }} 
          />
          <input 
            type="date" 
            value={endDate} 
            onChange={e => setEndDate(e.target.value)} 
            style={{ padding: '9px', borderRadius: '10px', border: '1px solid #eee', outline: 'none' }} 
          />

          {(startDate || endDate || selectedType !== 'all') && (
            <button 
              onClick={clearFilter} 
              style={{ padding: '10px 12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}
            >
              Clear
            </button>
          )}

          <button 
            onClick={exportExcel} 
            style={{ background: '#27ae60', color: '#fff', border: 'none', padding: '10px 15px', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            📥 Excel
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left', fontSize: '12px' }}>
              <th style={{ padding: '15px' }}>DELETED AT</th>
              <th>ITEM / INVOICE / PURCHASE ID</th>
              <th>TYPE</th>
              <th>AMOUNT / DETAILS</th>
              <th>DELETED BY</th>
              <th style={{ textAlign: 'center' }}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#888', fontSize: '14px' }}>
                  No deleted items found.
                </td>
              </tr>
            ) : (
              filteredItems.map(item => {
                const amount = item.totalCost || item.totalBill || item.refundAmount || item.amount || (item.deletedLog ? (item.deletedLog.costPrice || item.deletedLog.price || 0) * item.deletedLog.quantity : 0);
                const displayType = item.type === 'stock_log' ? 'Stock Log' : (item.originalCollection || item.type || 'General');
                
                return (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f9f9f9', transition: 'background 0.2s' }}>
                    <td style={{ padding: '15px', fontSize: '13px', color: '#555' }}>{formatDateTime(item.deletedAt)}</td>
                    <td>
                      <b style={{ color: '#333', fontSize: '14px' }}>{item.name || item.purchaseId || item.invoiceId || 'N/A'}</b>
                      {item.deletedItemType && <span style={{ fontSize: '12px', color: '#666', display: 'block' }}>{item.deletedItemType}</span>}
                      {item.sourceCol === 'deleted_products' && <span style={{ fontSize: '11px', color: '#e74c3c', display: 'block' }}>Deleted Product Record</span>}
                    </td>
                    <td>
                      <span style={{ padding: '5px 10px', background: '#f0edff', color: '#4a00e0', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold' }}>
                        {displayType}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 'bold', color: '#2c3e50' }}>৳ {formatTk(amount)}</div>
                      
                      {/* স্টক লগের ক্ষেত্রে পিস, রেট ও তারিখ */}
                      {item.type === 'stock_log' && item.deletedLog && (
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                          Qty: <b>{item.deletedLog.quantity} Pcs</b> | Rate: ৳ {formatTk(item.deletedLog.costPrice || item.deletedLog.price)} <br/>
                          Date: {formatDateTime(item.deletedLog.date)}
                        </div>
                      )}

                      {/* পারচেজ (Purchase) এর ক্ষেত্রে সাপ্লায়ার ও পিস বা ডেট */}
                      {item.originalCollection === 'purchases' && (
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                          {item.supplierName && <span>Supplier: <b>{item.supplierName}</b><br/></span>}
                          {item.date && <span>Date: {formatDateTime(item.date)}</span>}
                        </div>
                      )}

                      {/* সাধারণ প্রোডাক্ট বা অন্য আইটেম */}
                      {item.stock !== undefined && item.type !== 'stock_log' && (
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                          Stock: <b>{item.stock} Pcs</b> | Price: ৳ {formatTk(item.price)}
                        </div>
                      )}
                    </td>
                    <td style={{ color: '#555', fontSize: '13px' }}>{item.deletedBy || 'Sohan'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {isAdmin ? (
                        <button
                          onClick={() => handleRestore(item)}
                          style={{ padding: '6px 16px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', boxShadow: '0 2px 5px rgba(39, 174, 96, 0.2)' }}
                        >
                          Restore
                        </button>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#999' }}>Admin only</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default RecycleBin;